import React, { useState, useEffect } from "react";
import { BookOpen, Edit, Trash2, X, Save, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("mv_admin_token")}` } });

/**
 * AddressBookPicker - selettore rubrica indirizzi per form admin.
 * Props:
 *   prefix: "origin" | "dest"
 *   label: es. "Mittente" | "Destinatario"
 *   form, setForm: state del form padre
 *   clientEmail: per filtrare per cliente (opzionale)
 */
export default function AddressBookPicker({ prefix, label, form, setForm, clientEmail }) {
  const [addresses, setAddresses] = useState([]);
  const [open, setOpen] = useState(false);
  const [editAddr, setEditAddr] = useState(null);
  const [editForm, setEditForm] = useState({});

  const loadAddresses = () => {
    axios.get(`${API}/admin/addresses`, authHeaders()).then((r) => setAddresses(r.data)).catch(() => {});
  };
  useEffect(() => { loadAddresses(); }, []);

  // Filtra per cliente se selezionato, altrimenti mostra tutti
  const filtered = clientEmail
    ? addresses.filter((a) => a.client_email === clientEmail || a.client_email === "admin")
    : addresses;

  // Raggruppa per client_email
  const grouped = {};
  filtered.forEach((a) => {
    const key = a.client_email || "admin";
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(a);
  });

  const applyAddress = (addr) => {
    setForm({
      ...form,
      [`${prefix}_name`]: addr.name || "",
      [`${prefix}_company`]: addr.company || "",
      [`${prefix}_address`]: addr.address || "",
      [`${prefix}_city`]: addr.city || "",
      [`${prefix}_cap`]: addr.cap || "",
      [`${prefix}_province`]: addr.province || "",
      [`${prefix}_phone`]: addr.phone || "",
    });
    setOpen(false);
    toast.success(`Indirizzo "${addr.name} - ${addr.city}" applicato`);
  };

  const startEdit = (addr) => {
    setEditAddr(addr.id);
    setEditForm({ name: addr.name, company: addr.company || "", address: addr.address, city: addr.city, cap: addr.cap, province: addr.province || "", phone: addr.phone || "" });
  };

  const saveEdit = async (addrId) => {
    try {
      await axios.put(`${API}/admin/addresses/${addrId}`, editForm, authHeaders());
      toast.success("Indirizzo aggiornato");
      setEditAddr(null);
      loadAddresses();
    } catch { toast.error("Errore nell'aggiornamento"); }
  };

  const deleteAddr = async (addrId) => {
    if (!window.confirm("Eliminare questo indirizzo?")) return;
    try {
      await axios.delete(`${API}/admin/addresses/${addrId}`, authHeaders());
      toast.success("Indirizzo eliminato");
      loadAddresses();
    } catch { toast.error("Errore nell'eliminazione"); }
  };

  return (
    <div className="relative" data-testid={`address-picker-${prefix}`}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="inline-flex items-center gap-1.5 text-xs font-bold text-[#FF5200] hover:underline uppercase tracking-widest font-heading"
        data-testid={`address-picker-btn-${prefix}`}
      >
        <BookOpen size={12} /> Rubrica {label}
        <ChevronDown size={12} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="absolute z-30 left-0 top-full mt-1 w-full max-w-lg bg-white border border-zinc-200 shadow-lg max-h-72 overflow-y-auto" data-testid={`address-dropdown-${prefix}`}>
          {filtered.length === 0 ? (
            <p className="px-4 py-6 text-sm text-zinc-400 text-center">Nessun indirizzo salvato</p>
          ) : (
            Object.entries(grouped).map(([clientKey, addrs]) => (
              <div key={clientKey}>
                <div className="px-3 py-1.5 bg-zinc-50 border-b border-zinc-100">
                  <span className="text-[10px] font-bold tracking-widest uppercase text-zinc-400 font-heading">
                    {clientKey === "admin" ? "Indirizzi Admin" : clientKey}
                  </span>
                </div>
                {addrs.map((addr) => (
                  <div key={addr.id} className="border-b border-zinc-50 last:border-0">
                    {editAddr === addr.id ? (
                      /* Edit inline */
                      <div className="p-3 space-y-2 bg-orange-50/30">
                        <div className="grid grid-cols-2 gap-2">
                          <input type="text" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                            className="col-span-2 h-7 px-2 border border-zinc-200 rounded-sm text-xs focus:border-[#FF5200] outline-none" placeholder="Nome / Ragione sociale" />
                          <input type="text" value={editForm.address} onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
                            className="col-span-2 h-7 px-2 border border-zinc-200 rounded-sm text-xs focus:border-[#FF5200] outline-none" placeholder="Indirizzo" />
                          <input type="text" value={editForm.city} onChange={(e) => setEditForm({ ...editForm, city: e.target.value })}
                            className="h-7 px-2 border border-zinc-200 rounded-sm text-xs focus:border-[#FF5200] outline-none" placeholder="Citta" />
                          <div className="grid grid-cols-2 gap-2">
                            <input type="text" value={editForm.cap} onChange={(e) => setEditForm({ ...editForm, cap: e.target.value })}
                              className="h-7 px-2 border border-zinc-200 rounded-sm text-xs focus:border-[#FF5200] outline-none" placeholder="CAP" />
                            <input type="text" value={editForm.province} onChange={(e) => setEditForm({ ...editForm, province: e.target.value })}
                              className="h-7 px-2 border border-zinc-200 rounded-sm text-xs focus:border-[#FF5200] outline-none" placeholder="Prov" />
                          </div>
                          <input type="text" value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                            className="col-span-2 h-7 px-2 border border-zinc-200 rounded-sm text-xs focus:border-[#FF5200] outline-none" placeholder="Telefono" />
                        </div>
                        <div className="flex gap-2 justify-end">
                          <button type="button" onClick={() => setEditAddr(null)} className="text-xs text-zinc-400 hover:text-zinc-600 px-2 py-1">Annulla</button>
                          <button type="button" onClick={() => saveEdit(addr.id)} className="text-xs bg-[#FF5200] text-white px-3 py-1 rounded-sm hover:bg-[#e64a00] flex items-center gap-1">
                            <Save size={10} /> Salva
                          </button>
                        </div>
                      </div>
                    ) : (
                      /* Display */
                      <div className="flex items-center gap-2 px-3 py-2 hover:bg-zinc-50 group">
                        <button type="button" onClick={() => applyAddress(addr)} className="flex-1 text-left" data-testid={`address-select-${addr.id}`}>
                          <p className="text-sm font-medium text-[#09090B]">{addr.name}</p>
                          <p className="text-xs text-zinc-400">{addr.address}, {addr.cap} {addr.city} {addr.province ? `(${addr.province})` : ""}</p>
                        </button>
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button type="button" onClick={() => startEdit(addr)} className="p-1 text-zinc-400 hover:text-[#FF5200]" title="Modifica" data-testid={`address-edit-${addr.id}`}>
                            <Edit size={12} />
                          </button>
                          <button type="button" onClick={() => deleteAddr(addr.id)} className="p-1 text-zinc-400 hover:text-red-500" title="Elimina" data-testid={`address-delete-${addr.id}`}>
                            <Trash2 size={12} />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
